// Supabase implementations of the batch-1 provider methods (RPCs in 21_gavion_leads_rpc.sql, Task 15).
import type { SupabaseClient } from "@supabase/supabase-js";
import type { LeadDataProviderMethods, LeadImportResult } from "./types";

type Failure = { message: string; code?: string } | null;
/** Keeps the Postgres code; the RPCs' `forbidden` (P0001) becomes a 403 like an RLS refusal. */
const check = (error: Failure) => {
  if (error)
    throw Object.assign(new Error(error.message), {
      code: error.code,
      status: error.message === "forbidden" ? 403 : undefined,
    });
};

/** Phones per `in(...)` filter: keeps the PostgREST GET URL short (well under 1000). */
export const CHECK_PHONES_CHUNK = 200;

export function createLeadMethods(
  getClient: () => SupabaseClient,
): LeadDataProviderMethods {
  return {
    async importCompanies(rows, options) {
      const { data, error } = await getClient().rpc("import_companies", {
        p_rows: rows,
        p_options: options,
      });
      check(error);
      return data as LeadImportResult;
    },
    async checkLeadPhones(phones) {
      const existing: string[] = [];
      const suppressed: string[] = [];
      for (let i = 0; i < phones.length; i += CHECK_PHONES_CHUNK) {
        const part = phones.slice(i, i + CHECK_PHONES_CHUNK);
        const client = getClient();
        const [companies, blocked] = await Promise.all([
          client.from("companies").select("phone_e164").in("phone_e164", part),
          client
            .from("suppressions")
            .select("value")
            .eq("channel", "phone")
            .in("value", part),
        ]);
        check(companies.error);
        check(blocked.error);
        existing.push(
          ...((companies.data ?? []) as { phone_e164: string }[]).map(
            (r) => r.phone_e164,
          ),
        );
        suppressed.push(
          ...((blocked.data ?? []) as { value: string }[]).map((r) => r.value),
        );
      }
      return { existing, suppressed };
    },
    async assignLeads(ids, salesId) {
      const { data, error } = await getClient().rpc("assign_lead", {
        p_company_ids: ids.map(Number),
        p_sales_id: salesId === null ? null : Number(salesId),
      });
      check(error);
      return Number(data ?? 0);
    },
    async markDoNotCall(ids) {
      const { data, error } = await getClient().rpc("mark_do_not_call", {
        p_company_ids: ids.map(Number),
      });
      check(error);
      return Number(data ?? 0);
    },
    // GAVION call-brief-pipeline (84 §2): the RPC's refusal code stays the error's message (forbidden → 403)
    async sendToPipeline(companyId, stage, at) {
      const { data, error } = await getClient().rpc("send_to_pipeline", {
        p_company_id: Number(companyId),
        p_stage: stage,
        p_at: at,
      });
      check(error);
      return Number(data);
    },
  };
}
